import { Divider } from "@mui/material";
import { Tooltip, Drawer } from "antd";
import React, { Fragment, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import applocumDashbookLogo from "@/assets/images/applocum_dashbook_client.svg";
import logoIconBlue from "@/assets/images/logo_icon_blue.svg";
import SidebarIcons from "@/Components/Icons/SidebarIcons";
import { routingConstants } from "@/Constants/routingConstants";
import {
  toggleTermsConditionModal,
  toggleMobileDrawer,
  toggleSidebarsideBarCollapsed,
} from "@/reducers/clientcommon";
import {
  showManagedServiceToast,
  showRoleAccessToast,
} from "@/utilities/utils";

import SidebarProfile from "./profile/SidebarProfile";
import SidebarLink from "./SidebarLink";
import SidebarLinkDropdown from "./SidebarLinkDropdown";
import SidebarSubLink from "./SidebarSubLink";
import { TOOLTIPTEXT } from "../../../../utilities/CommonText";
import useIsMobile from "../../../../utilities/hooks/useIsMobile";
import { CalendarArrow } from "../../../Icons/CalendarArrow";
import { RejectIcon } from "../../../Icons/RejectIcon";
import NotAllowedButtonWrapper from "../managedService/NotAllowedButtonWrapper";
import NoAccessButtonWrapper from "../UserRoleAccess/NoAccessButtonWrapper";

/**
 * This is a functional component representing the client sidebar.
 *
 * @return {JSX.Element} The client sidebar component
 */
const ClientSidebar = () => {
  const dispatch = useDispatch();
  const { isPremiumAccount, clientUserAllPermissions } = useSelector(
    (state) => state.session,
  );
  const [schduleDropdownOpen, setSchduleDropdownOpen] = useState(false);
  const [candidateDropdownOpen, setCandidateDropdownOpen] = useState(false);
  const [timesheetDropdownOpen, setTimesheetDropdownOpen] = useState(false);
  const { user } = useSelector((state) => state.clientProfile);
  const { sideBarCollapsed, mobileDrawerOpen } = useSelector(
    (state) => state.clientCommon,
  );
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);
  const isMobile = useIsMobile();
  const isSmallMobile =
    typeof window !== "undefined" && window.innerWidth <= 412;

  // On mobile, sidebar should always render in expanded state
  const effectiveCollapsed = isMobile ? false : sideBarCollapsed;

  // Close mobile drawer on any navigation click
  const closeMobileDrawer = () => {
    if (isMobile) dispatch(toggleMobileDrawer(false));
  };

  const { showTermsConditionModal, showQrCodeModal } = useSelector(
    (state) => state.clientCommon,
  );

  // console.log("clientUserAllPermissions", clientUserAllPermissions)
  const sidebarLinks = [
    {
      mainType: "Booking",
      mainTypeLinks: [
        // {
        //   type: "postJob",
        //   text: "Post a Job",
        //   href: routingConstants.createJob,
        //   // has_access: user?.client_allow_publish_job,
        //   role_access: "is_allow_to_publish_job",
        // },
        {
          type: "schedule",
          text: "Schedule",
          height: "h-[135px]",
          cls: "tour-unfilled-clientsidebar",
          subLinksHeight: "group-hover/item:h-[135px]",
          subLinksActiveHeight: "group-hover:h-[135px]",
          href: "#",
          /**
           * A function that toggles the value of schduleDropdownOpen.
           *
           * @return {void}
           */
          func: () => {
            setSchduleDropdownOpen(!schduleDropdownOpen);
          },
          value: schduleDropdownOpen,
          subLinks: [
            {
              type: "unfilled",
              text: "Unfilled",
              href: routingConstants.unfilledContracts,
              role_access: "is_allow_to_unfilled_list",
            },
            {
              type: "upcoming",
              text: "Upcoming",
              href: routingConstants.bookedCandidate,
              role_access: "is_allow_to_booked_list",
            },
            {
              type: "past",
              text: "Past",
              href: routingConstants.workedCandidate,
              role_access: "is_allow_to_worked_list",
            },
          ],
        },
        {
          type: "candidates",
          text: "Candidates",
          href: "#",
          height: "h-[180px]",
          cls: "",
          subLinksHeight: "group-hover/item:h-[180px]",
          subLinksActiveHeight: "group-hover:h-[180px]",
          func: () => {
            setCandidateDropdownOpen(!candidateDropdownOpen);
          },
          value: candidateDropdownOpen,
          subLinks: [
            {
              text: "Invited",
              type: "invited",
              href: routingConstants.invitedCandidate,
              role_access: "is_allow_to_invited_list",
            },
            {
              text: "Proposed",
              type: "applied",
              href: routingConstants.appliedCandidate,
              class: `${!isPremiumAccount ? "opacity-50" : ""}`,
              role_access: "is_allow_to_applied_list",
            },
            {
              text: "Booked",
              type: "booked",
              href: routingConstants.bookedCandidate,
              role_access: "is_allow_to_booked_list",
            },
            {
              text: "Worked",
              type: "worked",
              href: routingConstants.workedCandidate,
              role_access: "is_allow_to_worked_list",
            },
          ],
        },
        {
          type: "timesheets",
          text: "Timesheet",
          height: "h-[180px]",
          subLinksHeight: "group-hover/item:h-[180px]",
          subLinksActiveHeight: "group-hover:h-[180px]",
          href: "#",
          /**
           * A function that toggles the value of schduleDropdownOpen.
           *
           * @return {void}
           */
          func: () => {
            setTimesheetDropdownOpen(!timesheetDropdownOpen);
          },
          value: timesheetDropdownOpen,
          subLinks: [
            {
              type: "assigned",
              text: "Assigned",
              href: routingConstants.assignedTimesheet,
              role_access: "is_allow_to_assigned_timesheet_list",
            },
            {
              type: "submitted",
              text: "Submitted",
              href: routingConstants.submittedTimesheet,
              role_access: "is_allow_to_submitted_timesheet_list",
            },
            {
              type: "authorised",
              text: "Authorised",
              href: routingConstants.authorisedTimesheet,
              role_access: "is_allow_to_authorised_timesheet_list",
            },
            {
              type: "terminated",
              text: "Disputed",
              href: routingConstants.terminatedTimesheet,
              role_access: "is_allow_to_terminated_timesheet_list",
            },
          ],
        },
        // {
        //   type: "qrCode",
        //   text: "QR Code",
        //   linkType: "button",
        //   role_access: "is_allow_to_qr_code",
        //   forPremiumOnly: true,
        //   isActive: showQrCodeModal,
        //   handleClick: () => {
        //     if (!isPremiumAccount) {
        //       dispatch(toggleOurServiceModal(true));
        //       return;
        //     } else {
        //       dispatch(toggleQrCodeModal(true));
        //     }
        //   },
        // },
      ],
    },
    {
      mainType: "Finance",
      mainTypeLinks: [
        // {
        //   type: "jobRates",
        //   text: "Job Rates",
        //   href: routingConstants.clientJobRates,
        //   has_access: isPremiumAccount ? (user?.is_bank_client ? true : false) : false,
        //   role_access: "is_allow_to_bank_jobrates",
        // },
        {
          type: "invoices",
          text: "Invoices",
          href: routingConstants.invoice,
          role_access: "is_allow_to_invoice_list",
        },
        {
          type: "termsCondition",
          text: "Terms & Conditions",
          linkType: "button",
          forPremiumOnly: false,
          isActive: showTermsConditionModal,
          handleClick: () => {
            dispatch(toggleTermsConditionModal(true));
          },
        },
        // {
        //   type: "downloads",
        //   text: "Downloads",
        //   href: routingConstants.downloads,
        // },
      ],
    },
  ];

  const { activePage, activeSubPage } = useSelector(
    (state) => state.clientCommon,
  );
  const getLinkTypeButton = (data) => {
    const { forPremiumOnly, handleClick, isActive, type, text } = data;
    return (
      <li
        className={`group/item transition-linear flex h-[42px] cursor-pointer items-center rounded-lg px-4 py-2 text-[14px] font-semibold !no-underline !duration-200 ${
          isActive
            ? "bg-primary-100 !text-primary"
            : `text-txtColor ${
                forPremiumOnly
                  ? isPremiumAccount
                    ? "!hover:text-primary text-txtColor hover:bg-grey"
                    : "cursor-not-allowed opacity-30"
                  : "!hover:text-primary text-txtColor hover:bg-grey"
              }`
        }`}
        onClick={() => {
          handleClick();
          closeMobileDrawer();
        }}
      >
        <div className="items transition-linear relative flex w-full justify-start group-hover:gap-4">
          {isActive ? (
            <SidebarIcons type={`${type}Active`} />
          ) : (
            <SidebarIcons type={type} />
          )}
          <span
            className={`transition-linear absolute left-[40px] top-[2px] inline-block ${effectiveCollapsed ? "w-0" : ""} overflow-hidden whitespace-nowrap break-keep group-hover:w-[calc(100%-48px)]`}
          >
            {text}
          </span>
        </div>
      </li>
    );
  };
  // Sidebar inner content — shared between mobile drawer and desktop fixed sidebar
  const sidebarInner = (
    <>
      <div
        className={`ClientSidebar_wrapper ${isMobile ? "expanded" : effectiveCollapsed ? "collapsed" : "expanded"} transition-linear group flex h-screen ${isMobile ? "w-full" : effectiveCollapsed ? "w-[88px] hover:w-[231px]" : "w-[231px]"} ${!isMobile ? "fixed left-0 top-0 z-[1032]" : ""} tour-clientsidebar-dashboard flex-col items-start bg-white pb-[24px] ${!isMobile ? "shadow-[0px_1px_6px_0px_rgba(0,0,0,0.10)]" : ""}`}
        onMouseEnter={() => !isMobile && setIsSidebarHovered(true)}
        onMouseLeave={() => {
          if (isMobile) return;
          setIsSidebarHovered(false);
          setSchduleDropdownOpen(false);
          setCandidateDropdownOpen(false);
          setTimesheetDropdownOpen(false);
        }}
      >
        {/* On mobile, logo is shown in the drawer header instead */}
        {!isMobile && (
          <div className="w-full pb-[20px] pt-[14px] text-left">
            <div
              className={`relative ${effectiveCollapsed ? "mx-[24px]" : "mx-2"} overflow-hidden text-left transition-all duration-300 group-hover:mx-2`}
            >
              <Link to={routingConstants.dashboard} className="inline-block">
                {/* Small Logo */}
                <img
                  src={logoIconBlue}
                  alt="logo"
                  className={`mx-auto h-[50px] w-[37px] transition-all duration-300 ${!effectiveCollapsed ? "opacity-0" : "opacity-100 group-hover:opacity-0"} `}
                />
                {/* Large Logo */}
                <img
                  src={applocumDashbookLogo}
                  alt="logo"
                  className={`absolute left-0 top-0 h-[50px] max-w-[200px] transition-all duration-300 ${!effectiveCollapsed ? "opacity-100" : "opacity-0 group-hover:opacity-100"} `}
                />
              </Link>
            </div>
          </div>
        )}

        {/* <Scrollbar
        style={{ width: "100%", height: "100%" }}
        trackYProps={{ style: { width: "4px !important", right: "3px" } }}
      > */}
        <div
          className={`transition-linear w-full grow ${effectiveCollapsed ? "px-4" : "px-2"} webkit-ClientSidebar-scroll group-hover:w-full group-hover:px-2`}
        >
          <ul>
            <li>
              <Link
                to={routingConstants.dashboard}
                onClick={closeMobileDrawer}
                className={`${!isMobile ? "tour-clientSidebar-dashboard" : ""} group/item transition-linear flex h-[42px] items-center !overflow-x-hidden rounded-lg px-4 py-2 text-[14px] font-semibold !no-underline !duration-200 ${
                  activePage === "dashboard"
                    ? "bg-primary-100 !text-primary"
                    : "!hover:text-primary text-txtColor hover:bg-grey"
                }`}
              >
                <div className="items transition-linear relative flex w-full justify-start group-hover:gap-4">
                  {activePage === "dashboard" ? (
                    <SidebarIcons type={"dashboardActive"} />
                  ) : (
                    <SidebarIcons type={"dashboard"} />
                  )}
                  <span
                    className={`transition-linear absolute left-[40px] top-[2px] inline-block ${effectiveCollapsed ? "w-0" : ""} overflow-hidden whitespace-nowrap break-keep group-hover:w-[calc(100%-48px)]`}
                  >
                    Dashboard
                  </span>
                </div>
              </Link>
            </li>
          </ul>
          <div
            className={`!group-hover:h-[21px] transition-linear ${effectiveCollapsed ? "!h-0" : "h-[21px] py-[10px]"} !h-0 overflow-hidden px-[7.5px] group-hover:py-[10px]`}
          >
            <Divider />
          </div>
          {sidebarLinks.map((ele, index) => {
            return (
              <div key={index}>
                <div
                  className={`transition-linear flex h-[0] items-start overflow-hidden px-[12px] ${effectiveCollapsed ? "" : "h-[30px]"} group-hover:h-[30px]`}
                >
                  <span className="inline-block h-[18px] text-[12px] font-bold text-primary">
                    {ele.mainType}
                  </span>
                </div>
                <ul className="flex flex-col gap-1">
                  {ele.mainTypeLinks.map((e, index) => {
                    // console.log("data--------->", e)
                    const hasRoleAccess =
                      clientUserAllPermissions?.[e.role_access];
                    const { has_access = true, linkType = "link" } = e;
                    if (e.subLinks) {
                      return (
                        <li key={index}>
                          <SidebarLinkDropdown
                            type={e.type}
                            text={e.text}
                            activePage={activePage}
                            key={index}
                            // to={e.href}
                            cls={e.cls || ""}
                            func={e.func}
                            isOpened={e.value}
                            isExanded={effectiveCollapsed}
                          >
                            {/* {activePage === e.type ? ( */}
                            <>
                              <ul
                                className={`relative ${e.value ? e.height : "h-0"} transition-linear after:bg-primay overflow-hidden before:absolute before:left-[28px] before:top-0 before:h-full before:w-[1px] before:bg-txtColor before:content-[''] after:absolute after:left-[27px] after:h-[42px] after:w-[3px] after:content-['']`}
                              >
                                {e.subLinks.map((sublnk, index) => {
                                  // console.log("sub----------------->", sublnk)
                                  const hasSubRoleAccess =
                                    clientUserAllPermissions?.[
                                      sublnk.role_access
                                    ];

                                  // let isPremiumAccountPage =
                                  //   sublnk.type == "unfilled" ||
                                  //   sublnk.type == "applied" ||
                                  //   sublnk.type == "terminated";
                                  let isPremiumAccountPage =
                                    sublnk.type == "unfilled" ||
                                    sublnk.type == "applied";

                                  let isTerminatedPage =
                                    sublnk.type == "terminated";
                                  let managedServicePremium = isPremiumAccount
                                    ? isPremiumAccount
                                    : user?.client_allow_publish_job;

                                  return (
                                    <li
                                      className="pl-[40px] pr-[10px]"
                                      key={index}
                                    >
                                      {(isPremiumAccountPage &&
                                        !managedServicePremium) ||
                                      (isTerminatedPage &&
                                        !isPremiumAccount) ? (
                                        <NotAllowedButtonWrapper
                                          rootClass="rounded-lg"
                                          iconClass="w-[20px] h-[20px] text-primary"
                                        >
                                          <SidebarSubLink
                                            text={sublnk.text}
                                            type={sublnk.type}
                                            activeSubPage={activeSubPage}
                                            isExanded={effectiveCollapsed}
                                            to={
                                              (isPremiumAccountPage &&
                                                !managedServicePremium) ||
                                              (isTerminatedPage &&
                                                !isPremiumAccount)
                                                ? "#"
                                                : sublnk.href
                                            }
                                            handleClick={(e) => {
                                              if (
                                                (isPremiumAccountPage &&
                                                  !managedServicePremium) ||
                                                (isTerminatedPage &&
                                                  !isPremiumAccount)
                                              ) {
                                                e.preventDefault();
                                                showManagedServiceToast();
                                              }
                                            }}
                                          />
                                        </NotAllowedButtonWrapper>
                                      ) : (
                                        <>
                                          {hasSubRoleAccess == 0 ||
                                          hasSubRoleAccess == "" ? (
                                            <NoAccessButtonWrapper
                                              key={ele.name}
                                              rootClass="rounded-lg"
                                              iconClass="w-[20px] h-[20px] text-primary"
                                              _checkPermission={[
                                                sublnk.role_access,
                                              ]}
                                            >
                                              <SidebarSubLink
                                                text={sublnk.text}
                                                type={sublnk.type}
                                                activeSubPage={activeSubPage}
                                                to={
                                                  (isPremiumAccountPage &&
                                                    !managedServicePremium) ||
                                                  (isTerminatedPage &&
                                                    !isPremiumAccount)
                                                    ? "#"
                                                    : sublnk.href
                                                }
                                                handleClick={(e) => {
                                                  if (
                                                    (isPremiumAccountPage &&
                                                      !managedServicePremium) ||
                                                    (isTerminatedPage &&
                                                      !isPremiumAccount)
                                                  ) {
                                                    e.preventDefault();
                                                    showManagedServiceToast();
                                                  }
                                                }}
                                              />
                                            </NoAccessButtonWrapper>
                                          ) : (
                                            <SidebarSubLink
                                              text={sublnk.text}
                                              type={sublnk.type}
                                              activeSubPage={activeSubPage}
                                              to={
                                                (isPremiumAccountPage &&
                                                  !managedServicePremium) ||
                                                (isTerminatedPage &&
                                                  !isPremiumAccount)
                                                  ? "#"
                                                  : sublnk.href
                                              }
                                              handleClick={(e) => {
                                                if (
                                                  (isPremiumAccountPage &&
                                                    !managedServicePremium) ||
                                                  (isTerminatedPage &&
                                                    !isPremiumAccount)
                                                ) {
                                                  e.preventDefault();
                                                  showManagedServiceToast();
                                                }
                                              }}
                                            />
                                          )}
                                        </>
                                      )}
                                    </li>
                                  );
                                })}
                              </ul>
                            </>
                            {/* ) : null} */}
                          </SidebarLinkDropdown>
                        </li>
                      );
                    } else {
                      if (linkType === "button") {
                        // console.log("Role access------>", e)
                        const hasRoleQRAccess =
                          clientUserAllPermissions?.[e.role_access];
                        const { forPremiumOnly = false } = e;
                        if (forPremiumOnly) {
                          if (isPremiumAccount) {
                            return (
                              <Fragment key={index}>
                                {hasRoleQRAccess === 0 ||
                                hasRoleQRAccess === "" ? (
                                  <NoAccessButtonWrapper
                                    rootClass="rounded-lg"
                                    iconClass="w-[20px] h-[20px] text-primary"
                                    // _element="li"
                                    _checkPermission={[e.role_access]}
                                  >
                                    {getLinkTypeButton(e)}
                                  </NoAccessButtonWrapper>
                                ) : (
                                  getLinkTypeButton(e)
                                )}
                              </Fragment>
                            );
                          } else {
                            return (
                              <Fragment key={index}>
                                <NotAllowedButtonWrapper
                                  rootClass="rounded-lg
                                  "
                                  iconClass="w-[20px] h-[20px] text-primary"
                                  // _element="li"
                                >
                                  {getLinkTypeButton(e)}
                                </NotAllowedButtonWrapper>
                              </Fragment>
                            );
                          }
                        } else {
                          return (
                            <Fragment key={index}>
                              {getLinkTypeButton(e)}
                            </Fragment>
                          );
                        }
                      } else {
                        if (has_access) {
                          let hasJobInvoiceAccess =
                            clientUserAllPermissions?.[e.role_access];

                          //this condition is to check if the user has access to post job or not & not changed flow so check this condition.
                          if (
                            e.type == "postJob" &&
                            (isPremiumAccount ||
                              !user.client_allow_publish_job) &&
                            (!isPremiumAccount || user.client_allow_publish_job)
                          ) {
                            return hasJobInvoiceAccess === 0 ||
                              hasJobInvoiceAccess === "" ? (
                              <li key={index}>
                                <NoAccessButtonWrapper
                                  key={ele.name}
                                  rootClass="rounded-lg"
                                  iconClass="w-[20px] h-[20px] text-primary"
                                  _checkPermission={[e.role_access]}
                                >
                                  <SidebarLink
                                    type={e.type}
                                    text={e.text}
                                    to={
                                      e.type == "postJob" &&
                                      !user?.client_allow_publish_job
                                        ? "#"
                                        : e.href
                                    }
                                    activePage={activePage}
                                    key={index}
                                    isExanded={effectiveCollapsed}
                                    handleClick={(event) => {
                                      if (
                                        e.type == "postJob" &&
                                        !isPremiumAccount &&
                                        !user?.client_allow_publish_job
                                      ) {
                                        event.preventDefault();
                                        showManagedServiceToast();
                                      }
                                    }}
                                  />
                                </NoAccessButtonWrapper>
                              </li>
                            ) : (
                              <li key={index}>
                                <NotAllowedButtonWrapper
                                  rootClass="rounded-lg"
                                  iconClass="w-[20px] h-[20px] text-primary"
                                >
                                  <SidebarLink
                                    type={e.type}
                                    text={e.text}
                                    to={
                                      e.type == "postJob" &&
                                      !user?.client_allow_publish_job
                                        ? "#"
                                        : e.href
                                    }
                                    activePage={activePage}
                                    key={index}
                                    isExanded={effectiveCollapsed}
                                    handleClick={(event) => {
                                      if (
                                        e.type == "postJob" &&
                                        !isPremiumAccount &&
                                        !user?.client_allow_publish_job
                                      ) {
                                        event.preventDefault();
                                        showManagedServiceToast();
                                      }
                                    }}
                                  />
                                </NotAllowedButtonWrapper>
                              </li>
                            );
                          } else {
                            // console.log("Post job & invoice-------", e.type)
                            return hasJobInvoiceAccess == 0 ||
                              hasJobInvoiceAccess == "undefined" ? (
                              <Fragment key={index}>
                                <NoAccessButtonWrapper
                                  rootClass="rounded-lg"
                                  iconClass="w-[20px] h-[20px] text-primary"
                                  _checkPermission={[e.role_access]}
                                >
                                  <li>
                                    <SidebarLink
                                      type={e.type}
                                      text={e.text}
                                      to={
                                        e.type == "postJob" &&
                                        !user?.client_allow_publish_job
                                          ? "#"
                                          : e.href
                                      }
                                      activePage={activePage}
                                      key={index}
                                      isExanded={effectiveCollapsed}
                                      handleClick={(event) => {
                                        if (
                                          e.type == "postJob" &&
                                          !isPremiumAccount &&
                                          !user?.client_allow_publish_job
                                        ) {
                                          event.preventDefault();
                                          showManagedServiceToast();
                                        }
                                      }}
                                    />
                                  </li>
                                </NoAccessButtonWrapper>
                              </Fragment>
                            ) : (
                              // for not access client to post job if client is premium.
                              <li
                                key={index}
                                className={`${e.type == "postJob" && !user.client_allow_publish_job ? "!cursor-not-allowed" : ""}`}
                              >
                                <SidebarLink
                                  type={e.type}
                                  text={e.text}
                                  to={
                                    e.type == "postJob" &&
                                    !user.client_allow_publish_job
                                      ? "#"
                                      : e.href
                                  }
                                  activePage={activePage}
                                  _className={`${e.type == "postJob" && !user.client_allow_publish_job ? "opacity-50 hover:opacity-50 hover:bg-grey  !cursor-not-allowed" : ""}`}
                                  key={index}
                                  isExanded={effectiveCollapsed}
                                  handleClick={(event) => {
                                    if (
                                      e.type == "postJob" &&
                                      !isPremiumAccount &&
                                      !user?.client_allow_publish_job
                                    ) {
                                      event.preventDefault();
                                      showManagedServiceToast();
                                    }
                                    if (
                                      e.type == "postJob" &&
                                      !user.client_allow_publish_job
                                    ) {
                                      event.preventDefault();
                                      showRoleAccessToast();
                                      return;
                                    }
                                  }}
                                />
                              </li>
                            );
                          }
                        }
                      }
                    }
                  })}
                </ul>
                {index !== sidebarLinks.length - 1 ? (
                  <div
                    className={`!group-hover:h-[21px] transition-linear ${effectiveCollapsed ? "!h-0" : "h-[21px] py-[10px]"} !h-0 overflow-hidden px-[7.5px] group-hover:py-[10px]`}
                  >
                    <Divider />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
        {/* </Scrollbar> */}
        <SidebarProfile />
      </div>
    </>
  );

  // Mobile: render sidebar inside a Drawer
  if (isMobile) {
    return (
      <Drawer
        placement="left"
        open={mobileDrawerOpen}
        onClose={() => dispatch(toggleMobileDrawer(false))}
        width={isSmallMobile ? "100%" : 350}
        styles={{ body: { padding: 0 } }}
        zIndex={1032}
        destroyOnClose={false}
        title={null}
        closable={false}
      >
        <div className="flex items-center justify-between px-4 py-3">
          <Link
            to={routingConstants.dashboard}
            onClick={closeMobileDrawer}
            className="inline-block"
          >
            <img
              src={applocumDashbookLogo}
              alt="logo"
              className="h-[40px] max-w-[170px]"
            />
          </Link>
          <button
            className="grid h-[40px] w-[40px] shrink-0 place-items-center rounded-lg text-primary hover:bg-grey"
            onClick={() => dispatch(toggleMobileDrawer(false))}
          >
            <RejectIcon cls="w-[30px] h-[30px]" />
          </button>
        </div>
        {sidebarInner}
      </Drawer>
    );
  }

  // Desktop: fixed sidebar with collapse toggle
  return (
    <>
      <Tooltip
        title={`${sideBarCollapsed ? TOOLTIPTEXT?.Expand : TOOLTIPTEXT?.Collapse} Sidebar`}
        placement="right"
      >
        <button
          className={`group fixed top-6 z-[1033] hidden h-[28px] w-[28px] translate-x-1/2 place-items-center rounded-2xl border-2 border-lightblueBG bg-white text-center !shadow-[0px_1px_6px_0px_rgba(0,0,0,0.10)] hover:bg-primary md:grid ${!sideBarCollapsed || isSidebarHovered ? "left-[203px]" : "left-[60px]"} transition-all duration-100`}
          onClick={() => {
            dispatch(toggleSidebarsideBarCollapsed(!sideBarCollapsed));
          }}
        >
          <CalendarArrow
            cls={`transition-linear w-auto h-[12px] w-[12px]  ${!sideBarCollapsed ? "text-primary group-hover:text-white" : "text-primary group-hover:text-white rotate-180"}`}
          />
        </button>
      </Tooltip>
      {sidebarInner}
    </>
  );
};

export default ClientSidebar;
